import axios from 'axios';
import { LoginRequest, LoginResponse } from '../types/auth';
import { StatisticsApiResponse, StatisticsComparisonApiResponse, StatisticsRequest, StatisticsComparisonData } from '../types/statistics';

//const API_BASE_URL = 'https://localhost:7001/api'; // Debug locale
const API_BASE_URL = 'https://gest-api.betatest.easy-diet.it/api';

// Crea un'istanza di axios con configurazione base
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor per aggiungere il token JWT alle richieste
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor per gestire risposte e errori
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token scaduto o non valido - rimuovi dal localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      
      // Reindirizza al login se non siamo già lì
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Servizi API per l'autenticazione
export const authApi = {
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    try {
      const response = await api.post<LoginResponse>('/auth/login', credentials);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        message: 'Errore di connessione al server'
      };
    }
  },

  logout: async (): Promise<void> => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Errore durante il logout:', error);
    } finally {
      // Rimuovi sempre i dati locali
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
    }
  },

  refreshToken: async (): Promise<LoginResponse> => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      const response = await api.post<LoginResponse>('/auth/refresh', refreshToken);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        message: 'Errore durante il refresh del token'
      };
    }
  }
};

// Servizi API per le statistiche
export const statisticsApi = {
  getDailyStats: async (request: StatisticsRequest): Promise<StatisticsApiResponse> => {
    try {
      const response = await api.post<StatisticsApiResponse>('/statistics/daily', request);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        data: {
          data: [],
          metadata: {
            startDate: '',
            endDate: '',
            totalDays: 0,
            generatedAt: new Date().toISOString()
          }
        },
        message: error.message || 'Errore durante il recupero delle statistiche giornaliere',
        errors: [],
        timestamp: new Date().toISOString()
      };
    }
  },

  getWeeklyStats: async (request: StatisticsRequest): Promise<StatisticsApiResponse> => {
    try {
      const response = await api.post<StatisticsApiResponse>('/statistics/weekly', request);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        data: {
          data: [],
          metadata: {
            startDate: '',
            endDate: '',
            totalDays: 0,
            generatedAt: new Date().toISOString()
          }
        },
        message: error.message || 'Errore durante il recupero delle statistiche settimanali',
        errors: [],
        timestamp: new Date().toISOString()
      };
    }
  },

  getStatsWithComparison: async (request: StatisticsRequest): Promise<StatisticsComparisonApiResponse | null> => {
    try {
      const response = await api.post<StatisticsComparisonApiResponse>('/statistics/comparison', request);
      return response.data;
    } catch (error: any) {
      console.error('Errore durante il recupero delle statistiche con confronto:', error);
      return null;
    }
  }
};

export default api;